// Creación de una suscripción RECURRENTE de Mobbex (gateway AR), etapa de
// PRUEBA. Espejo conceptual de `mpSubscription.ts`.
//
// FLUJO REAL (cuando haya cuenta + credenciales): el backend crea la
// suscripción (plan), el suscriptor, y devuelve los ids con los que el front
// abre el componente Embed de Mobbex. Esos pasos usan el API Key + Access
// Token SECRETOS, así que NO pueden correr en el front.
//
// FLUJO ACTUAL (sin backend): mientras `MOBBEX_SUBSCRIPTION_URL` esté vacío,
// este helper devuelve un resultado MOCK para poder ver los estados de la UI
// (éxito / rechazo) sin pegarle a la red. La página detecta `mock: true` y
// simula el checkout en vez de abrir el iframe real de Mobbex.

import { MOBBEX_MOCK_RESULT, MOBBEX_SUBSCRIPTION_URL } from '../config';

export interface MobbexSubscriptionParams {
  /** Monto del cobro recurrente, en la unidad de la moneda (ARS). Ej: 10000. */
  total: number;
  /** Moneda. Ej: 'ARS'. */
  currency: string;
  /** Recurrencia. Por ahora solo mensual. */
  interval: 'monthly';
  /** Referencia única de la prueba. */
  reference?: string;
  payer?: { name?: string; email?: string };
}

export type MobbexStatus = 'authorized' | 'rejected' | 'error';

export interface MobbexSubscriptionResult {
  status: MobbexStatus;
  /** id de la suscripción (plan) — para abrir el Embed en modo real. */
  subscriptionId?: string;
  /** id del suscriptor (sid) — para abrir el Embed en modo real. */
  subscriberId?: string;
  /** true si el resultado vino del mock (no hay backend todavía). */
  mock?: boolean;
  /** Mensaje legible para mostrar inline en rechazo/error. */
  message?: string;
  /** Respuesta cruda (para debug en la página de prueba). */
  raw?: unknown;
}

/** true cuando no hay backend configurado → corre el mock. */
export const isMobbexMock = !MOBBEX_SUBSCRIPTION_URL;

const DEFAULT_REJECT_MESSAGE =
  'No pudimos procesar la suscripción. Revisá los datos o probá con otra tarjeta.';

/**
 * Pide al backend crear la suscripción recurrente y devuelve los ids para
 * abrir el Embed. Normaliza la respuesta a `{ status }`. Nunca lanza: ante
 * fallo devuelve `{ status: 'error' }`.
 */
export async function createMobbexSubscription(
  params: MobbexSubscriptionParams,
): Promise<MobbexSubscriptionResult> {
  // ── Mock de desarrollo (sin backend / sin credenciales) ──────────────────
  if (!MOBBEX_SUBSCRIPTION_URL) {
    await new Promise((r) => setTimeout(r, 1000)); // simula latencia
    if (MOBBEX_MOCK_RESULT === 'rejected') {
      return { status: 'rejected', mock: true, message: DEFAULT_REJECT_MESSAGE };
    }
    return {
      status: 'authorized',
      mock: true,
      subscriptionId: 'mock-sub-0001',
      subscriberId: 'mock-sbr-0001',
      raw: { mock: true, ...params },
    };
  }

  // ── Real (futuro): POST al endpoint que habla con la API de Mobbex ───────
  try {
    const res = await fetch(MOBBEX_SUBSCRIPTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    let data: any = null;
    try {
      data = await res.json();
    } catch {
      /* respuesta sin body JSON */
    }

    if (!res.ok) {
      const isAuthError = res.status === 400;
      return {
        status: isAuthError ? 'rejected' : 'error',
        message: data?.message || DEFAULT_REJECT_MESSAGE,
        raw: data,
      };
    }

    // Convención esperada del backend: { subscriptionId, subscriberId } (o
    // { id, sid }). Se ajustará cuando exista el endpoint real.
    return {
      status: 'authorized',
      subscriptionId: data?.subscriptionId ?? data?.id,
      subscriberId: data?.subscriberId ?? data?.sid,
      raw: data,
    };
  } catch (err) {
    return {
      status: 'error',
      message: (err as Error)?.message || 'Error de red al crear la suscripción.',
      raw: err,
    };
  }
}
